import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { HeaderIconL } from "../../../assets/components/HeaderGroup";
import { FillButton, GrayButton } from "../../../assets/components/ButtonGroup";
import left from "../../../assets/icons/Arrow/left.svg";
import { fetchConciergeImagesByBusinessNumber } from "../../../utils/conciergeImages";

export default function StoredImagePicker() {
  const navigate = useNavigate();
  const location = useLocation();
  const baseState = location.state || {};

  const storeBusinessNumber = useMemo(() => {
    return baseState?.store_business_number || localStorage.getItem("store_business_number") || "";
  }, [baseState?.store_business_number]);

  const [images, setImages] = useState([]);
  const [selectedFileId, setSelectedFileId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoading(true);
        const result = await fetchConciergeImagesByBusinessNumber(storeBusinessNumber);
        if (!alive) return;

        const nextImages = Array.isArray(result.images) ? result.images : [];
        setImages(nextImages);
        if (nextImages.length > 0) {
          setSelectedFileId(nextImages[0].file_id);
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [storeBusinessNumber]);

  const selectedImage = useMemo(() => {
    return images.find((image) => image.file_id === selectedFileId) || null;
  }, [images, selectedFileId]);

  const handleConfirm = () => {
    if (!selectedImage) return;

    navigate("/manual/marketing/gallery", {
      replace: true,
      state: {
        ...baseState,
        startStep: 2,
        imageUri: `${process.env.REACT_APP_FASTAPI_ADS_URL}/concierge/images/${selectedImage.file_id}/file`,
        imagePreview: selectedImage.public_url || "",
        initialImageFromMenu: true,
      },
    });
  };

  return (
    <div className="min-h-dvh bg-surface-default flex flex-col">
      <HeaderIconL title="저장한 이미지 선택" icon={left} onIconClick={() => navigate(-1)} />

      <div className="flex-1 px-4 pt-4 pb-6 overflow-y-auto">
        <div className="pb-4">
          <h2 className="text-t2 text-text-default">컨시어지에 저장한 이미지를 선택해 주세요</h2>
          <p className="mt-1 text-b2 text-text-subtler">
            저장한 사진 중 1장만 선택해서 바로 이미지 생성에 사용할 수 있어요.
          </p>
        </div>

        {loading ? (
          <div className="rounded-3xl bg-cg-50 px-5 py-10 text-center text-b2 text-text-subtler">
            저장한 이미지를 불러오는 중입니다.
          </div>
        ) : images.length === 0 ? (
          <div className="rounded-3xl bg-cg-50 px-5 py-10 text-center">
            <p className="text-b1 text-text-default">저장한 이미지가 없습니다.</p>
            <p className="mt-1 text-c1 text-text-subtler">
              내 정보 수정이나 컨시어지 등록에서 이미지를 먼저 추가해 주세요.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {images.map((image) => {
              const isSelected = image.file_id === selectedFileId;

              return (
                <button
                  key={image.file_id}
                  type="button"
                  onClick={() => setSelectedFileId(image.file_id)}
                  className={`overflow-hidden rounded-[24px] border text-left transition ${
                    isSelected
                      ? "border-[#7864F9] ring-2 ring-[#7864F9]/20"
                      : "border-border-subtle bg-white"
                  }`}
                >
                  <div className="relative aspect-square bg-cg-50">
                    {image.public_url ? (
                      <img
                        src={image.public_url}
                        alt="저장한 매장 이미지"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center text-c1 text-text-subtler">
                        이미지 없음
                      </div>
                    )}
                    <div className="absolute left-3 top-3">
                      <span
                        className={`inline-flex h-7 min-w-7 items-center justify-center rounded-full px-2 text-c1 font-bold ${
                          isSelected ? "bg-[#7864F9] text-white" : "bg-white/90 text-text-subtle"
                        }`}
                      >
                        {isSelected ? "선택" : image.file_order || ""}
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {images.length === 0 ? (
        <GrayButton onClick={() => navigate(-1)}>돌아가기</GrayButton>
      ) : (
        <FillButton onClick={handleConfirm} disabled={!selectedImage}>
          선택한 이미지로 진행하기
        </FillButton>
      )}
    </div>
  );
}
